/**
 * Shader effects of extra.json at chart time t (W1-08; formats-and-packaging.md P§5.1-P§5.2, ARCHITECTURE.md
 * §7.3 step 5):
 *
 *  - times come from the extra.json's own BPM list (the lowered IR always writes the chart's), built in list order
 *    exactly like Phira's BpmList;
 *  - an effect is active for t in [sec(start), sec(end));
 *  - uniforms in the order Phira sets them after the shader defaults: every variable (`null` = Phira's empty anim,
 *    whose `now()` is 0, so the uniform is set to 0), evaluated with the extra.json keyframe rules (extraAnim.ts);
 *    `time` (chart seconds), `screenTexture`, `screenSize` and `UVScale` are the renderer's;
 *  - non-global effects go to `effects` (after notes, before hit effects), global ones to `globalEffects` (over the
 *    HUD), each in effect order;
 *  - a built-in shader name maps to `{ kind: 'builtin' }`; a "/path" shader to the package asset (one leading '/'
 *    stripped) through the context's `assetIdForPath` (see index.ts); an effect whose shader cannot be resolved
 *    is not drawn.
 */

import { BUILTIN_SHADER_NAMES } from '../../model/schema';
import { createTempoMap } from '../../time/tempo';
import type { Sec, TempoMap } from '../../time/types';
import type { IrEffect, IrExtraJson, IrExtraKeyframe } from '../../ir/types';
import type { AssetId, BuiltinShader, EffectId, EffectShader } from '../../model/types';
import type { EffectDraw, FrameDrawListBuilder } from '../types';
import { compileVar, varAt, type CompiledVar, type ExtraValue } from './extraAnim';

const BUILTIN: readonly BuiltinShader[] = BUILTIN_SHADER_NAMES;

/** Maps a package path (no leading '/') to its asset, or null when the package has no such file. */
export type AssetResolver = (path: string) => AssetId | null;

export interface CompiledEffect {
  readonly id: EffectId;
  /** Built-in shader, or the package path of a custom one. */
  readonly shader:
    | { readonly kind: 'builtin'; readonly name: BuiltinShader }
    | { readonly kind: 'path'; readonly path: string }
    | null;
  readonly startSec: Sec;
  readonly endSec: Sec;
  readonly global: boolean;
  readonly vars: ReadonlyArray<readonly [string, CompiledVar]>;
}

/** The tempo of an extra.json (its `bpm` list, in list order). */
export function extraTempo(extra: IrExtraJson): TempoMap {
  return createTempoMap(extra.bpm.map((b) => ({ bpm: b.bpm, startTime: b.time })));
}

function shaderOf(name: string): CompiledEffect['shader'] {
  if (name.startsWith('/')) return name.length > 1 ? { kind: 'path', path: name.slice(1) } : null;
  const builtin = BUILTIN.find((b) => b === name);
  return builtin === undefined ? null : { kind: 'builtin', name: builtin };
}

export function compileEffect(e: IrEffect, index: number, tempo: TempoMap): CompiledEffect {
  const sec = (t: readonly [number, number, number]): Sec => tempo.secAtTriple(t);
  return {
    id: e.srcEffect ?? (index as EffectId),
    shader: shaderOf(e.shader),
    startSec: sec(e.start),
    endSec: sec(e.end),
    global: e.global,
    vars: e.vars.map(
      ([name, v]) => [name, compileVar(v as null | ExtraValue | readonly IrExtraKeyframe<ExtraValue>[], sec)] as const,
    ),
  };
}

export function compileEffects(extra: IrExtraJson, tempo: TempoMap): CompiledEffect[] {
  return extra.effects.map((e, i) => compileEffect(e, i, tempo));
}

/** Is the effect active at t (Phira: start <= t < end)? */
export function effectActive(e: CompiledEffect, t: Sec): boolean {
  return t >= e.startSec && t < e.endSec;
}

/** The uniforms of an effect at t (variables only; `time` is EffectDraw.time). */
export function effectUniforms(e: CompiledEffect, t: Sec): Array<readonly [string, number | readonly number[]]> {
  return e.vars.map(([name, v]) => [name, varAt(v, t, 0)] as const);
}

/** Fills `out.effects` and `out.globalEffects` with the effects active at t, in effect order. */
export function fillEffects(
  effects: readonly CompiledEffect[],
  t: Sec,
  resolve: AssetResolver | null,
  out: Pick<FrameDrawListBuilder, 'effects' | 'globalEffects'>,
): void {
  for (const e of effects) {
    if (!effectActive(e, t) || e.shader === null) continue;
    let shader: EffectShader;
    if (e.shader.kind === 'builtin') shader = { kind: 'builtin', name: e.shader.name };
    else {
      const asset = resolve === null ? null : resolve(e.shader.path);
      if (asset === null) continue;
      shader = { kind: 'asset', asset };
    }
    const draw: EffectDraw = { effect: e.id, shader, uniforms: effectUniforms(e, t), time: t, global: e.global };
    if (e.global) out.globalEffects.push(draw);
    else out.effects.push(draw);
  }
}
